/**
 * v109.9.14 — Travel Advances tab
 * -------------------------------
 * Shows the CURRENT USER'S own travel advances (Master Admin sees their own
 * here too — the full firehose lives in the "All" tab).
 *
 * • Progressive status label:
 *     Submitted → Approved By 1 User → Approved By 2 User → …→ Approved
 * • Approver names are intentionally hidden (only aggregate progress is visible).
 * • Action column: Edit icon ONLY while the record is Submitted AND no approver
 *   has approved a step yet. Locks as soon as the first approval lands.
 * • Displays unsettled amount + 10-day settlement warning.
 */
import { useEffect, useState, useMemo } from "react";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Plus, Wallet, AlertTriangle, Pencil, Lock, Trash2 } from "lucide-react";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import TravelAdvanceFormDialog from "../components/TravelAdvanceFormDialog";
import { useDataChange } from "../lib/events";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

const STATUS_TONE = {
  Rejected:  "bg-rose-100 text-rose-700 border-rose-200",
  Approved:  "bg-emerald-100 text-emerald-800 border-emerald-200",
  Disbursed: "bg-blue-100 text-blue-800 border-blue-200",
  Settled:   "bg-violet-100 text-violet-800 border-violet-200",
};

function daysBetween(iso) {
  if (!iso) return 0;
  const d = new Date(iso);
  if (isNaN(d)) return 0;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

/** Compute the progressive status pill for a travel advance row. */
export function progressiveStatus(row) {
  const s = row.status || "";
  if (s === "Rejected" || s === "Approved" || s === "Disbursed" || s === "Settled") {
    return { label: s, tone: STATUS_TONE[s] || "bg-slate-100" };
  }
  const approvedCount = (row.approvals || []).filter((a) => a.status === "approved").length;
  if (approvedCount === 0) {
    return { label: "Submitted", tone: "bg-amber-100 text-amber-800 border-amber-200" };
  }
  return {
    label: `Approved By ${approvedCount} User${approvedCount > 1 ? "s" : ""}`,
    tone: "bg-cyan-100 text-cyan-800 border-cyan-200",
  };
}

/** Returns true if the owner is allowed to edit this advance right now. */
export function canOwnerEdit(row) {
  if ((row.status || "") !== "Submitted") return false;
  const anyApproved = (row.approvals || []).some((a) => a.status === "approved");
  return !anyApproved;
}

/** v109.9.173 — Owner may delete their advance until it's Approved. */
export function canOwnerDelete(row) {
  return !["Approved", "Disbursed", "Settled"].includes(row.status || "");
}

export default function TravelAdvancesTab({ actionPulse = null }) {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  const [rows, setRows] = useState([]);
  const [unsettled, setUnsettled] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const myUid = String(user?.id || "");

  const load = async () => {
    setBusy(true);
    try {
      const [{ data: recs }, { data: uns }] = await Promise.all([
        api.get("/records/accounts_advances"),
        api.get("/travel-advances/unsettled-summary?scope=self").catch(() => ({ data: [] })),
      ]);
      // Only the current user's own advances — master admin sees rest in "All" tab.
      const own = (Array.isArray(recs) ? recs : []).filter((r) => (r.created_by_id || "") === myUid);
      setRows(own);
      setUnsettled((uns || [])[0] || null);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  // v109.9.179 — Auto-refresh when Accounts or approvers change any
  // Travel record (advance / settlement) elsewhere in the app.
  useDataChange(["travel:updated", "travel:advance:updated", "travel:disbursed", "travel:ledgered"], () => load());

  // React to Dashboard "Apply Advance" quick-action — auto-open the form.
  useEffect(() => {
    if (actionPulse && actionPulse.key === "new") {
      setEditRow(null);
      setFormOpen(true);
    }
  }, [actionPulse]);

  // v109.9.173 — Owner delete (available until row is Approved).
  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/records/accounts_advances/${confirmDelete.id}`, {
        data: { reason: "Owner deletion" },
      });
      toast.success("Travel advance deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const summary = useMemo(() => ({
    total: rows.length,
    submitted: rows.filter((r) => r.status === "Submitted").length,
    approved: rows.filter((r) => r.status === "Approved").length,
    disbursed: rows.filter((r) => r.status === "Disbursed").length,
  }), [rows]);

  return (
    <div className="space-y-4">
      {/* Header: unsettled + create */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SummaryTile
            icon={<Wallet size={14} />}
            label="Unsettled Amount"
            value={`₹ ${Number(unsettled?.unsettled || 0).toLocaleString("en-IN")}`}
            tone={unsettled?.unsettled > 0 ? "amber" : "slate"}
            testid="unsettled-tile"
          />
          {unsettled?.overdue_count > 0 && (
            <SummaryTile
              icon={<AlertTriangle size={14} />}
              label="Overdue > 10 days"
              value={String(unsettled.overdue_count)}
              tone="rose"
              testid="overdue-tile"
            />
          )}
          <SummaryTile label="Submitted"  value={String(summary.submitted)}  tone="amber"   testid="cnt-submitted"  icon={<span className="inline-block w-2 h-2 rounded-full bg-amber-500" />} />
          <SummaryTile label="Approved"   value={String(summary.approved)}   tone="emerald" testid="cnt-approved"   icon={<span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />} />
          <SummaryTile label="Disbursed"  value={String(summary.disbursed)}  tone="blue"    testid="cnt-disbursed"  icon={<span className="inline-block w-2 h-2 rounded-full bg-blue-500" />} />
        </div>
        <Button
          onClick={() => { setEditRow(null); setFormOpen(true); }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="new-travel-advance-btn"
        >
          <Plus size={14} className="mr-1.5" /> Raise Travel Advance
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
        {viewMode === "card" ? (
          rows.length === 0 ? (
            <div className="p-8 text-center text-slate-500">{busy ? "Loading…" : "You have no advances yet — click Raise Travel Advance to create one."}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3" data-testid="travel-advances-cards">
              {rows.map((r) => {
                const editable = canOwnerEdit(r);
                const { label, tone } = progressiveStatus(r);
                return (
                  <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`tadv-card-${r.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 dark:text-white truncate">{r.travel_purpose}</div>
                        {r.bypass_crm && <Badge className="mt-1 bg-rose-100 text-rose-700 border-rose-200 text-[9.5px]">CRM Bypass</Badge>}
                      </div>
                      <Badge className={`${tone} text-[10px] shrink-0`}>{label}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">From</div><div className="text-slate-800 text-[10.5px]">{(r.from_datetime || r.from_date || "").replace("T"," ").slice(0,16)}</div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">To</div><div className="text-slate-800 text-[10.5px]">{(r.to_datetime || r.to_date || "").replace("T"," ").slice(0,16)}</div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Days</div><div className="mono text-slate-800">{r.days_count || 0}</div></div>
                      <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Amount</div><div className="mono font-semibold text-slate-900">₹ {Number(r.advance_amount || 0).toLocaleString("en-IN")}</div></div>
                      <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Flow</div><div><Badge variant="outline" className="text-[10.5px]">{r.approval_type || "—"}</Badge></div></div>
                    </div>
                    {r.status === "Approved" && r.approved_at && daysBetween(r.approved_at) > 10 && (
                      <div className="text-[10px] text-amber-700 mb-2">10-day settlement due</div>
                    )}
                    <div className="pt-2 border-t border-slate-100 flex items-center justify-end gap-1.5">
                      {editable ? (
                        <Button size="sm" variant="outline" onClick={() => { setEditRow(r); setFormOpen(true); }} className="h-7 text-[10.5px]" data-testid={`tadv-edit-card-${r.id}`}>
                          <Pencil size={11} className="mr-1" /> Edit
                        </Button>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10.5px] text-slate-500" data-testid={`tadv-locked-card-${r.id}`}>
                          <Lock size={10} /> Locked
                        </span>
                      )}
                      {canOwnerDelete(r) && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setConfirmDelete(r)}
                          className="h-7 text-[10.5px] text-rose-600 border-rose-200 hover:bg-rose-50"
                          data-testid={`tadv-delete-card-${r.id}`}
                        >
                          <Trash2 size={11} className="mr-1" /> Delete
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="travel-advances-table">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-[11px] uppercase tracking-wider text-slate-600 dark:text-slate-300">
              <tr>
                <th className="p-2.5 text-left">Purpose</th>
                <th className="p-2.5 text-left">From → To</th>
                <th className="p-2.5 text-right">Days</th>
                <th className="p-2.5 text-right">Amount</th>
                <th className="p-2.5 text-center">Flow</th>
                <th className="p-2.5 text-center">Status</th>
                <th className="p-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-slate-500">
                  {busy ? "Loading…" : "You have no advances yet — click Raise Travel Advance to create one."}
                </td></tr>
              ) : rows.map((r) => {
                const editable = canOwnerEdit(r);
                const { label, tone } = progressiveStatus(r);
                return (
                  <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/60 dark:hover:bg-slate-800/40" data-testid={`tadv-row-${r.id}`}>
                    <td className="p-2.5">
                      {r.travel_purpose}
                      {r.bypass_crm ? <Badge className="ml-1.5 bg-rose-100 text-rose-700 border-rose-200 text-[9.5px]">CRM Bypass</Badge> : null}
                    </td>
                    <td className="p-2.5">
                      <div>{(r.from_datetime || r.from_date || "").replace("T", " ").slice(0, 16)}</div>
                      <div className="text-slate-500">→ {(r.to_datetime || r.to_date || "").replace("T", " ").slice(0, 16)}</div>
                    </td>
                    <td className="p-2.5 text-right">{r.days_count || 0}</td>
                    <td className="p-2.5 text-right font-mono font-semibold">₹ {Number(r.advance_amount || 0).toLocaleString("en-IN")}</td>
                    <td className="p-2.5 text-center">
                      <Badge variant="outline" className="text-[10.5px]">
                        {r.approval_type || "—"}
                      </Badge>
                    </td>
                    <td className="p-2.5 text-center">
                      <Badge className={`${tone} text-[10.5px]`}>{label}</Badge>
                      {r.status === "Approved" && r.approved_at && daysBetween(r.approved_at) > 10 && (
                        <div className="text-[9.5px] text-amber-700 mt-0.5">10-day settlement due</div>
                      )}
                    </td>
                    <td className="p-2.5 text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        {editable ? (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => { setEditRow(r); setFormOpen(true); }}
                            className="h-7 w-7 p-0"
                            title="Edit advance"
                            data-testid={`tadv-edit-${r.id}`}
                          >
                            <Pencil size={12} />
                          </Button>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10.5px] text-slate-500" data-testid={`tadv-locked-${r.id}`}>
                            <Lock size={11} /> Locked
                          </span>
                        )}
                        {canOwnerDelete(r) && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => setConfirmDelete(r)}
                            className="h-7 w-7 p-0 text-rose-600 border-rose-200 hover:bg-rose-50"
                            title="Delete advance"
                            data-testid={`tadv-delete-${r.id}`}
                          >
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <TravelAdvanceFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditRow(null); }}
        onCreated={load}
        editRecord={editRow}
      />

      {/* v109.9.173 — Owner-side delete confirmation. */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <DialogContent className="max-w-md" data-testid="tadv-delete-dialog">
          <DialogHeader>
            <DialogTitle className="heading text-rose-700">Delete Travel Advance</DialogTitle>
          </DialogHeader>
          <div className="py-2 text-[12.5px] text-slate-700 dark:text-slate-200">
            You&apos;re about to permanently delete your advance for{" "}
            <b>{confirmDelete?.travel_purpose}</b> (₹{Number(confirmDelete?.advance_amount || 0).toLocaleString("en-IN")}).
            This cannot be undone.
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              onClick={doDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="tadv-delete-confirm"
            >
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryTile({ icon, label, value, tone = "slate", testid }) {
  const toneCls = {
    slate:  "border-slate-200 bg-white text-slate-800 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200",
    amber:  "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-200",
    rose:   "border-rose-300 bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:border-rose-800 dark:text-rose-200",
    indigo: "border-indigo-300 bg-indigo-50 text-indigo-900 dark:bg-indigo-950/40 dark:border-indigo-800 dark:text-indigo-200",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-200",
    blue:    "border-blue-300 bg-blue-50 text-blue-900 dark:bg-blue-950/40 dark:border-blue-800 dark:text-blue-200",
  }[tone];
  return (
    <div className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 ${toneCls}`} data-testid={testid}>
      <span className="opacity-70">{icon}</span>
      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
        <div className="text-[14px] font-bold leading-tight">{value}</div>
      </div>
    </div>
  );
}
